import type { FC } from "react";
import { memo } from "react";

import { DateDropdown } from "@ui/components/DateDropdown/DateDropdown";
import { FormField } from "@ui/components/Form/FormField";
import { TextInput } from "@ui/components/Inputs/TextInput/TextInput";
import { Txt } from "@ui/components/Txt/Txt";
import { useTranslations } from "@ui/providers/useTranslations";

import { SupportingText } from "@jti/core-components/components/SupportingText";
import type { AgeVerificationMethodType } from "@jti/fe-common";

import styles from "./FieldsArray.module.scss";

type FieldsArrayProps = {
  fieldName?(name: string): string;
} & AgeVerificationMethodType;

export const FieldsArray: FC<FieldsArrayProps> = memo(({ fieldName = (name: string) => name, ...method }) => {
  const { __ } = useTranslations();
  const renderOptionalInfo = () => {
    if (method.isOptional) {
      return (
        <div className={styles.optionalInfoIcon}>
          <SupportingText variant="info">{__("auth.registration.age_verification.optional_message")}</SupportingText>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.formField}>
      {method.fields.map((item) => {
        const ageVerificationLabel = __(`age_verification.form.${item.code}.label`, [], item.label);
        if (item.type === "date") {
          return (
            <div key={item.code}>
              <div className={styles.dobBlock}>
                <Txt className={styles.infoSupp}>
                  {ageVerificationLabel} {item.is_required ? "*" : ""}
                </Txt>
                <DateDropdown
                  fieldName={`avpValues.${item.code}`}
                  dropdownFieldPrefix={item.code}
                  required={item.is_required}
                />
              </div>
            </div>
          );
        }
        return (
          <div key={item.code}>
            <FormField
              key={item.code}
              id={fieldName(item.code)}
              name={fieldName(item.code)}
              label={ageVerificationLabel}
              showCustomError
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id={fieldName(item.code)}
                  data-testid={item.code}
                  placeholder={__(`age_verification.form.${item.code}.hint`, [], item.hint)}
                  autoComplete="off"
                  containerClassName={styles.limitMaxWidth}
                  isError={fieldState.invalid}
                  errorText={fieldState.error?.message || ""}
                />
              )}
              validation={{
                required: {
                  value: item.is_required && !method.isOptional,
                  message: __(
                    `auth.age_verification.method_fields.${item.code}.validation.field_required`,
                    undefined,
                    __("validation.field_required")
                  ),
                },
                ...(item.validation && {
                  pattern: {
                    value: new RegExp(item.validation),
                    message: __("ageconfirmation.pattern.error"),
                  },
                }),
              }}
              defaultValue=""
            />
            {renderOptionalInfo()}
          </div>
        );
      })}
    </div>
  );
});
